/**
 * overlay-stack — wspólny stan okien nakładkowych (`Overlay` i zbudowane na nim `Modal`,
 * `Drawer`, `PreviewModal`).
 *
 * Trzy rzeczy, które muszą być jedne dla całej aplikacji, a nie per okno:
 *   1. Zablokowanie przewijania strony pod oknem — z LICZNIKIEM. Dawniej każde okno zapamiętywało
 *      i przywracało `body.style.overflow` samo; przy zamknięciu dwóch zagnieżdżonych okien
 *      w kolejności „zewnętrzne pierwsze" strona zostawała zablokowana na stałe.
 *   2. Stos otwartych okien — żeby Esc i Tab, gdy fokus wypadł na `<body>` (np. po kliknięciu
 *      w tło okna, które się nie zamyka), trafiały do okna z wierzchu, a nie do nikogo.
 *   3. Jeden selektor elementów, na które można ustawić fokus.
 *
 * Esc i Tab z fokusem WEWNĄTRZ okna obsługuje sam komponent (React `onKeyDown` na tle). Ten moduł
 * dokłada tylko awaryjny nasłuch na `document` dla zdarzeń z `<body>`.
 */

/** Elementy, na które można ustawić fokus (pułapka fokusu, fokus początkowy). */
export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

// --- Blokada przewijania z licznikiem -------------------------------------------------------

let lockCount = 0;
let savedOverflow = '';

/**
 * Blokuje przewijanie strony pod oknem. Zwraca idempotentną funkcję zwalniającą blokadę;
 * wartość `overflow` sprzed pierwszej blokady wraca dopiero po zwolnieniu ostatniej.
 */
export function acquireBodyScrollLock(): () => void {
  if (lockCount === 0) {
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  lockCount += 1;

  let released = false;
  return () => {
    if (released) return;
    released = true;
    lockCount -= 1;
    if (lockCount === 0) document.body.style.overflow = savedOverflow;
  };
}

// --- Trwające przeciąganie (dnd-kit) --------------------------------------------------------

/**
 * Uchwyt przeciąganego elementu dnd-kit ma na czas przeciągania `aria-pressed="true"` oraz
 * `aria-roledescription` „draggable"/„sortable" (czujnik myszy i klawiatury).
 *
 * ZAŁOŻENIE: dnd-kit ustawia `aria-pressed` tylko przy domyślnej roli uchwytu (`button`), więc
 * NIE nadpisuj `attributes.role` ani `roleDescription` na uchwytach w oknach — Esc w trakcie
 * przeciągania zamknąłby wtedy okno. Przy czujniku z `activationConstraint` (odległość/opóźnienie)
 * atrybuty pojawiają się dopiero po aktywacji przeciągania.
 */
const ACTIVE_DRAG_SELECTOR = [
  '[aria-roledescription="draggable"][aria-pressed="true"]',
  '[aria-roledescription="sortable"][aria-pressed="true"]',
].join(',');

/**
 * Czy trwa przeciąganie dnd-kit. Okno w tym czasie NIE przechwytuje Esc: dnd-kit słucha go na
 * `document` i anuluje nim przeciąganie (a okno zostaje otwarte). Dzięki temu nie trzeba
 * podpinać obsługi Esc w każdym `DndContext` z osobna.
 *
 * `scope` zawęża szukanie uchwytu do panelu okna (przeciąganie POD oknem, np. na stronie, nie
 * odbiera oknu Esc); bez niego szuka w całym dokumencie.
 *
 * Koszt: w trakcie przeciągania Esc dociera do WSZYSTKICH nasłuchów `document` (okno go nie
 * zatrzymuje) — np. do zamykania szuflady/flyoutu w `Sidebar`. Dotyczy tylko przeciągania
 * klawiaturą przy jednocześnie otwartej szufladzie (myszą zamknąłby ją `mousedown` poza nią).
 */
export function isDragInProgress(scope: ParentNode = document): boolean {
  return scope.querySelector(ACTIVE_DRAG_SELECTOR) !== null;
}

/**
 * Czy trwa przeciąganie dnd-kit z uchwytem w KTÓRYMKOLWIEK z podanych zakresów. Okno używa tego
 * z własnym zakresem: panel okna + zarejestrowane portale (`useOverlayPortalScopeRef`) — o tym,
 * czy Esc należy do przeciągania, rozstrzyga miejsce AKTYWNEGO UCHWYTU, a nie cel zdarzenia
 * (fokus mógł się przenieść z uchwytu gdzie indziej w oknie albo na `<body>`). Brak zakresów
 * = brak przeciągania „w tym oknie".
 */
export function isDragInProgressInScopes(scopes: Iterable<ParentNode>): boolean {
  for (const scope of scopes) {
    if (isDragInProgress(scope)) return true;
  }
  return false;
}

// --- Stos okien i awaryjny nasłuch klawiatury -----------------------------------------------

export interface OverlayStackEntry {
  /** Panel okna — cel przeniesienia fokusu przy Tab, gdy fokus jest na `<body>`. */
  getPanel: () => HTMLElement | null;
  /**
   * Zakresy, w których uchwyt przeciągania dnd-kit „należy do tego okna": panel i portale
   * zarejestrowane przez okno. Bez tego liczy się sam panel (a bez panelu — cały dokument).
   */
  getDragScopes?: () => readonly ParentNode[];
  /** Czy to okno dopuszcza zamknięcie klawiszem Esc (`closeOnEsc`). */
  isEscapeAllowed: () => boolean;
  /** Prośba o zamknięcie klawiszem Esc (może przejść przez zabezpieczenie niezapisanych zmian). */
  dismissOnEscape: () => void;
}

const stack: OverlayStackEntry[] = [];

function handleDocumentKeyDown(e: KeyboardEvent): void {
  // Fokus w oknie obsługuje jego własny `onKeyDown` (zatrzymuje zdarzenie). Tu tylko `<body>`.
  if (e.defaultPrevented) return;
  if (e.target !== document.body && e.target !== document.documentElement) return;
  const top = stack[stack.length - 1];
  if (!top) return;

  if (e.key === 'Escape') {
    // Trwa przeciąganie: Esc należy do dnd-kit (anulowanie), okno go nie rusza. Ta sama reguła
    // co w `onKeyDown` okna (cel zdarzenia nie ma znaczenia): liczy się uchwyt w panelu okna
    // albo w jego zarejestrowanym portalu; przeciąganie POD oknem ani w obcym portalu nie
    // odbiera oknu Esc.
    if (isDragInProgressInScopes(top.getDragScopes?.() ?? [top.getPanel() ?? document])) return;
    // `preventDefault` także gdy okno Esc ignoruje: skróty strony pod oknem (np. odznaczanie
    // elementu w kreatorze procesów) nie mogą zadziałać „przez" otwarte okno.
    e.preventDefault();
    if (top.isEscapeAllowed()) top.dismissOnEscape();
    return;
  }

  if (e.key === 'Tab') {
    const panel = top.getPanel();
    if (!panel) return;
    e.preventDefault();
    const focusables = panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    const target = e.shiftKey ? focusables[focusables.length - 1] : focusables[0];
    (target ?? panel).focus();
  }
}

/**
 * Czy jakiekolwiek okno jest teraz otwarte. Skróty klawiszowe stron pod oknem (np. kreator
 * procesów: Delete, Ctrl+Z) sprawdzają to, żeby nie działać „przez" okno — także gdy fokus jest
 * na `<body>` albo w polu okna, którego nie obejmuje ich własny strażnik.
 */
export function hasOpenOverlay(): boolean {
  return stack.length > 0;
}

/**
 * Rejestruje okno na stosie (wierzch = ostatnio otwarte). Zwraca funkcję wyrejestrowującą —
 * usuwa wpis po tożsamości, więc jest bezpieczna przy zamykaniu w dowolnej kolejności
 * i przy podwójnych efektach StrictMode.
 */
export function registerOverlay(entry: OverlayStackEntry): () => void {
  stack.push(entry);
  if (stack.length === 1) document.addEventListener('keydown', handleDocumentKeyDown);

  return () => {
    const index = stack.indexOf(entry);
    if (index >= 0) stack.splice(index, 1);
    if (stack.length === 0) document.removeEventListener('keydown', handleDocumentKeyDown);
  };
}
